import {
	PatientTreatmentPlanStageLog,
	StageLogStatus,
} from "../../../../../../types/healthcare/TreatmentPlanTypes";

/**
 * Per-loop-attempt helpers over a plan's stage logs.
 *
 * A looping plan re-enters the same stages on each iteration, producing multiple stage logs that
 * share a stage_key but carry a distinct `loop_iteration`. These functions group/select those logs
 * so the UI can render the CURRENT attempt rather than collapsing all attempts together.
 *
 * `loop_iteration` is optional (backend B3) — when absent it defaults to 0, so a single-attempt plan
 * behaves exactly as before.
 */

/** Terminal statuses — a stage that has finished its attempt and won't change. */
const TERMINAL_STATUSES: ReadonlySet<StageLogStatus> = new Set<StageLogStatus>([
	"completed",
	"skipped",
	"failed",
]);

const stageKeyOf = (log: PatientTreatmentPlanStageLog): string | undefined =>
	log.stage_snapshot?.stage_key ?? log.stage?.stage_key;

const iterationOf = (log: PatientTreatmentPlanStageLog): number => log.loop_iteration ?? 0;

/** Sort two logs by loop_iteration ascending, falling back to id ascending. */
const byIterationThenId = (a: PatientTreatmentPlanStageLog, b: PatientTreatmentPlanStageLog): number => {
	const di = iterationOf(a) - iterationOf(b);
	if (di !== 0) return di;
	return (a.id ?? 0) - (b.id ?? 0);
};

/**
 * Group logs by stage_key, each group sorted by loop_iteration (fallback id) ascending.
 */
export function groupLogsByStageKey(
	logs: PatientTreatmentPlanStageLog[],
): Map<string, PatientTreatmentPlanStageLog[]> {
	const groups = new Map<string, PatientTreatmentPlanStageLog[]>();
	logs.forEach((log) => {
		const key = stageKeyOf(log);
		if (!key) return;
		const bucket = groups.get(key);
		if (bucket) bucket.push(log);
		else groups.set(key, [log]);
	});
	groups.forEach((bucket) => bucket.sort(byIterationThenId));
	return groups;
}

/**
 * The current attempt per stage_key — the highest-iteration log (fallback highest id) for each key.
 */
export function currentAttemptByStageKey(
	logs: PatientTreatmentPlanStageLog[],
): Map<string, PatientTreatmentPlanStageLog> {
	const groups = groupLogsByStageKey(logs);
	const current = new Map<string, PatientTreatmentPlanStageLog>();
	groups.forEach((bucket, key) => {
		// bucket is sorted ascending → last element is the highest iteration / id.
		current.set(key, bucket[bucket.length - 1]);
	});
	return current;
}

/**
 * The highest-iteration NON-TERMINAL log — the attempt the plan is actively sitting on.
 * Completed / skipped / failed prior attempts are ignored so a fresh loop-back shows as live.
 */
export function pickActiveLog(
	logs: PatientTreatmentPlanStageLog[],
): PatientTreatmentPlanStageLog | undefined {
	const nonTerminal = logs.filter((l) => !TERMINAL_STATUSES.has(l.status));
	if (nonTerminal.length === 0) return undefined;
	return nonTerminal.reduce((a, b) => (byIterationThenId(a, b) <= 0 ? b : a));
}

/** 1-based attempt number for display — loop_iteration + 1, defaulting to 1 when absent. */
export function attemptNumber(log: PatientTreatmentPlanStageLog): number {
	return iterationOf(log) + 1;
}
